// ============================================================================
// PRICE API SERVICE
// Fetches recent 30-day price data from asset-specific APIs
// ============================================================================

import { DATA_CONFIG } from '../config/dataConfig';

// Cache for API data
let apiCache = {};
let apiCacheTimestamp = {};

/**
 * Fetch recent 30-day price data from asset-specific API
 * @param {string} assetId - Asset identifier (gold, bitcoin, silver)
 * @returns {Promise<Array>} Array of {date, price} objects
 */
export const fetchRecentApiData = async (assetId) => {
    try {
        // Check if API is configured for this asset
        const apiConfig = DATA_CONFIG.apis[assetId];

        if (!apiConfig || !apiConfig.enabled) {
            console.warn(`[API] API not configured for ${assetId}. Skipping API fetch.`);
            return [];
        }

        // Check cache first
        if (DATA_CONFIG.cache.enabled && isCacheValid(assetId)) {
            console.log(`[API] Using cached data for ${assetId}`);
            return apiCache[assetId];
        }

        console.log(`[API] Fetching recent data for ${assetId} from ${apiConfig.provider}`);

        // Fetch data based on provider
        let data = [];

        switch (apiConfig.provider) {
            case 'coingecko':
                data = await fetchFromCoinGecko(assetId, apiConfig);
                break;
            case 'metals-dev':
                data = await fetchFromMetalsDev(assetId, apiConfig);
                break;
            default:
                console.warn(`[API] Unknown provider: ${apiConfig.provider}`);
                return [];
        }

        // Cache the data
        if (DATA_CONFIG.cache.enabled && data.length > 0) {
            apiCache[assetId] = data;
            apiCacheTimestamp[assetId] = Date.now();
        }

        console.log(`[API] Successfully fetched ${data.length} records for ${assetId}`);
        return data;

    } catch (error) {
        console.error(`[API] Error fetching data for ${assetId}:`, error);

        // Return cached data if available, even if expired
        if (apiCache[assetId]) {
            console.warn(`[API] Returning stale cached data for ${assetId}`);
            return apiCache[assetId];
        }

        return []; // Return empty array instead of throwing
    }
};

/**
 * Fetch data from CoinGecko API (for cryptocurrencies)
 */
const fetchFromCoinGecko = async (assetId, config) => {
    const days = DATA_CONFIG.recentDataDays;
    const url = `${config.baseUrl}${config.endpoint}?vs_currency=usd&days=${days}&interval=daily`;

    const response = await fetch(url);

    if (!response.ok) {
        throw new Error(`CoinGecko API error: ${response.statusText}`);
    }

    const data = await response.json();
    const prices = data.prices || [];

    // Convert to standard format
    return prices.map(([timestamp, price]) => ({
        date: new Date(timestamp).toISOString().split('T')[0],
        price: parseFloat(price.toFixed(2))
    }));
};

/**
 * Fetch data from Metals.dev API (for precious metals)
 */
const fetchFromMetalsDev = async (assetId, config) => {
    // This is a placeholder - actual implementation depends on Metals.dev API structure
    // User will need to provide the correct endpoint and parameters

    const days = DATA_CONFIG.recentDataDays;
    const url = `${config.baseUrl}${config.endpoint}`;

    const headers = {};
    if (config.apiKey) {
        headers['Authorization'] = `Bearer ${config.apiKey}`;
    }

    const response = await fetch(url, { headers });

    if (!response.ok) {
        throw new Error(`Metals.dev API error: ${response.statusText}`);
    }

    const data = await response.json();

    // TODO: Parse response based on actual API structure
    // This is a placeholder that needs to be updated when user provides API details
    console.warn('[API] Metals.dev parser not fully implemented. Needs API structure details.');

    return [];
};

/**
 * Check if cached data is still valid
 */
const isCacheValid = (assetId) => {
    if (!apiCache[assetId] || !apiCacheTimestamp[assetId]) {
        return false;
    }

    const age = Date.now() - apiCacheTimestamp[assetId];
    return age < DATA_CONFIG.cache.ttl;
};

/**
 * Clear cache for specific asset or all assets
 */
export const clearCache = (assetId = null) => {
    if (assetId) {
        delete apiCache[assetId];
        delete apiCacheTimestamp[assetId];
        console.log(`[API] Cache cleared for ${assetId}`);
    } else {
        apiCache = {};
        apiCacheTimestamp = {};
        console.log('[API] All cache cleared');
    }
};
